import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { alphanumericRegex } from 'constants/regex'
import { Modal } from '@jana-technology/platform-ui'

import { ISavedStateFilteredRoute } from '$/types'
import { useAppSelector } from 'app/hooks'
import { useAddQRASavedStateMutation } from 'app/services/api/qra/savedstates'
import { selectFilteredNetworkLines } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import { ErrorCode } from 'app/middlewares/notification'
import LoadingSpinner from 'common/components/LoadingSpinner'

import { WISCreateModalQueryString } from 'features/RiskManagement/Assessments/Scenarios/WISCreateModalQueryString'

export interface ISaveStateModal {
  isModalOpen: boolean
  isWISCreate?: boolean
  closeModal: () => void
}

const SaveStateModal = ({
  isModalOpen,
  isWISCreate = false,
  closeModal
}: ISaveStateModal) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const filteredNetworkLines = useAppSelector(selectFilteredNetworkLines)
  const nameInputDOM = useRef<HTMLInputElement>(null)
  const [addSavedState, { isLoading: isSaving }] = useAddQRASavedStateMutation()
  const [stateName, setStateName] = useState('')
  const [isStateNameValid, setIsStateNameValid] = useState(false)
  const [isStateNameTaken, setIsStateNameTaken] = useState(false)
  const rmParentPath = pathname.slice(0, pathname.indexOf('/risk-management'))

  useEffect(() => {
    setIsStateNameTaken(false)
  }, [isModalOpen])

  const saveState = (): void => {
    if (nameInputDOM?.current?.value == null) {
      return
    }

    const savedStateContinuousRoutes: ISavedStateFilteredRoute[] =
      filteredNetworkLines.map((line) => {
        return {
          savedStateId: line.id,
          continuousRouteId: line.id
        }
      })

    addSavedState({ name: stateName, savedStateContinuousRoutes })
      .unwrap()
      .then((state) => {
        const savedStatePath = `${rmParentPath}/risk-management/assessments/states/${state.id}`
        const searchParams = new URLSearchParams(search)

        if (isWISCreate) {
          searchParams.set(WISCreateModalQueryString.IsOpen, 'true')
        }

        navigate(`${savedStatePath}?${searchParams.toString()}`)
        closeModal()
      })
      .catch((err) => {
        if (err.data?.errors[0].code === ErrorCode.NotUnique) {
          setIsStateNameTaken(true)
        } else {
          closeModal()
        }
      })
  }

  const handleStateNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setStateName(event.target.value)

    const stateNameDOMElement = nameInputDOM.current

    if (stateNameDOMElement == null) {
      return
    }

    setIsStateNameValid(stateNameDOMElement.checkValidity())
    setIsStateNameTaken(false)
  }

  const SaveStateModalFooter = () => {
    return (
      <div className='grid grid-cols-2 gap-4'>
        <button
          className='h-10 w-24 font-semibold text-gray-700'
          onClick={closeModal}
        >
          CANCEL
        </button>
        <button
          className={[
            'h-10 w-24',
            'rounded bg-lighthouseBlue',
            'font-semibold text-white',
            'disabled:opacity-50'
          ].join(' ')}
          onClick={saveState}
          disabled={!isStateNameValid || stateName.length === 0}
        >
          SAVE
        </button>
      </div>
    )
  }

  return (
    <Modal
      title='Save State'
      data-testid='Save State Modal'
      actions={{ primary: <SaveStateModalFooter /> }}
      open={isModalOpen}
    >
      <LoadingSpinner isLoading={isSaving}>
        <div className='mx-1 flex flex-col'>
          {isWISCreate && (
            <div className='mb-4 text-gray-500'>
              You can only run a What-if Scenario analysis on a Saved State risk
              baseline.
            </div>
          )}
          <label
            className='mb-2 text-lg font-semibold'
            htmlFor='save-state-name-input'
          >
            Name
          </label>
          <input
            className='rounded-sm'
            id='save-state-name-input'
            type='text'
            ref={nameInputDOM}
            onChange={handleStateNameChange}
            pattern={alphanumericRegex}
            maxLength={20}
          />
          <div className='mt-1 text-sm font-medium text-red'>
            {!isStateNameValid &&
              stateName.length > 0 &&
              'State name can only contain alphanumeric characters and underscore'}
            {isStateNameTaken && 'State name already taken'}
          </div>
        </div>
      </LoadingSpinner>
    </Modal>
  )
}

export default SaveStateModal
